/**
 * OAuth Callback Page
 *
 * Handles the Google OAuth redirect callback.
 * Supports two flows:
 * 1. PKCE: reads `code` from URL query params → exchanges via backend
 * 2. Implicit: reads `access_token` + `refresh_token` from URL hash → sends to backend
 *
 * Then redirects to dashboard on success or login on failure.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Parse hash fragment into key-value pairs.
 * e.g., "#access_token=abc&refresh_token=def" → { access_token: 'abc', refresh_token: 'def' }
 */
function parseHashParams(hash: string): Record<string, string> {
  if (!hash || hash.length <= 1) return {};
  const params = new URLSearchParams(hash.substring(1));
  const result: Record<string, string> = {};
  for (const [key, value] of params.entries()) {
    result[key] = value;
  }
  return result;
}

// Module-level set of OAuth credentials we've already started processing.
// Lives at module scope (not component scope or useRef) so that even when
// the component fully unmounts and remounts — which happens because
// AuthContext's state change after a successful login causes a parent to
// re-render and recreate this route's component instance — we still know
// not to re-consume an already-used authorization code. Each Supabase
// authorization code is single-use; the second POST always 401s and shows
// a misleading "Login Failed" toast.
const processedCredentials = new Set<string>();

export default function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleOAuthCallback, handleOAuthTokens } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const hashParams = parseHashParams(window.location.hash);
    const hashAccessToken = hashParams.access_token;
    const hashRefreshToken = hashParams.refresh_token;

    // Identifier for this specific OAuth attempt: the code (PKCE) or the
    // access token (implicit). If we've seen it before, skip — a remount
    // is trying to re-consume credentials that were already exchanged.
    const credentialKey = code || hashAccessToken || null;
    if (credentialKey && processedCredentials.has(credentialKey)) {
      console.log('[OAuth Callback] Credentials already processed — skipping duplicate');
      return;
    }
    if (credentialKey) {
      processedCredentials.add(credentialKey);
    }

    // Clear both hash and ?code immediately so even if the effect re-fires
    // for any reason, there's nothing left in the URL to process.
    if (window.location.hash || window.location.search) {
      window.history.replaceState(null, '', window.location.pathname);
    }

    const processCallback = async () => {
      try {
        if (code) {
          console.log('[OAuth Callback] PKCE flow — exchanging code for session...');
          await handleOAuthCallback(code);
        } else if (hashAccessToken && hashRefreshToken) {
          console.log('[OAuth Callback] Implicit flow — sending tokens to backend...');
          await handleOAuthTokens(hashAccessToken, hashRefreshToken);
        } else {
          // No code/tokens in the URL. The usual cause is a *remount* of this
          // route right after a successful exchange — AuthContext's state
          // change re-renders the parent, which recreates this component, by
          // which point the first mount has already consumed and cleared the
          // URL. So this isn't an error: just send them on to the dashboard
          // (its auth guard bounces genuinely-unauthenticated visitors to
          // /login). Don't show an error or schedule a stray /login redirect.
          console.log('[OAuth Callback] No code/tokens in URL (likely a remount) — continuing to dashboard');
          navigate('/dashboard', { replace: true });
          return;
        }

        console.log('[OAuth Callback] Session created, navigating to dashboard');
        navigate('/dashboard', { replace: true });
      } catch (err) {
        console.error('[OAuth Callback] Exchange failed:', err);
        setError('Failed to complete Google sign-in. Please try again.');
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };

    processCallback();
    // Empty deps: callbacks captured here are good enough for a one-shot
    // operation that runs immediately on mount. New OAuth attempts always
    // come via a fresh page load (Google redirect), which remounts the
    // component and resets processedRef to false.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="text-center">
        {error ? (
          <>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 mb-4">
              <span className="text-destructive text-2xl">!</span>
            </div>
            <p className="text-sm text-destructive mb-2">{error}</p>
            <p className="text-xs text-muted-foreground">Redirecting to login...</p>
          </>
        ) : (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">
              Completing sign-in...
            </p>
          </>
        )}
      </div>
    </div>
  );
}
